import { useEffect, useState } from 'react'
import type React from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import type { DefaultValues } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, useReducedMotion } from 'motion/react'
import { ChevronDown, FileText, Info, ClipboardList } from 'lucide-react'
import { useFormStore } from '../store/formStore'
import { scrollToFirstError, FIRST_INVALID_FIELD } from '../lib/scrollToError'
import { listApplications } from '../api/applicant'
import { applicationReference } from './MyApplicationsScreen'
import { PageShell } from '../components/layout/PageShell'
import { FormField } from '../components/fields/FormField'
import { inputClassName } from '../components/fields/fieldStyles'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { SaveIndicator } from '../components/ui/SaveIndicator'
import { WelcomeWizard } from '../components/ui/WelcomeWizard'

// ---------------------------------------------------------------------------
// Zod Schema
// ---------------------------------------------------------------------------
const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  jobTitle: z.string().min(1, 'Job title is required'),
  companyWebsite: z
    .string()
    .min(1, 'Company website is required')
    .refine(
      (val) => {
        try {
          new URL(val.startsWith('http') ? val : `https://${val}`)
          return true
        } catch {
          return false
        }
      },
      { message: 'Please enter a valid website URL (e.g., https://example.com).' }
    ),
})

type FormData = z.infer<typeof schema>

// ---------------------------------------------------------------------------
// Document checklist data
// ---------------------------------------------------------------------------
const DOCS = [
  {
    title: 'Certificate of Incorporation or Registration',
    description: "Your company's official registration document. You can usually get it from the company registry where the business was incorporated.",
    required: true,
  },
  {
    title: 'Company license',
    description: 'Your regulatory license, if applicable. This is usually issued by the relevant financial regulator, such as CySEC, FCA, MFSA, or other.',
    required: false,
  },
  {
    title: 'Certificate of Incumbency or Certificate of Shareholders & Directors',
    description: "A document showing your company's current directors and shareholders. You can usually get it from your registered agent or company secretary. If this is not available, you can provide a Certificate of Shareholders and a Certificate of Directors instead.",
    required: true,
  },
  {
    title: 'Shareholder passports',
    description: 'A valid passport for each shareholder who owns more than 10% of the company, if applicable.',
    required: false,
  },
  {
    title: 'Director passports',
    description: 'A valid passport for each company director, if applicable.',
    required: false,
  },
  {
    title: 'Proof of address',
    description: 'A recent proof of address (e.g. utility bill or bank statement) for each shareholder, if applicable.',
    required: false,
  },
]

// ---------------------------------------------------------------------------
// WelcomeScreen
// ---------------------------------------------------------------------------
export function WelcomeScreen() {
  const navigate = useNavigate()
  const store = useFormStore()
  const shouldReduceMotion = useReducedMotion()

  // Save feedback is driven by the real auto-save (useAutoSave → store).
  const saveState = store.saveState
  const [checklistOpen, setChecklistOpen] = useState(false)
  // Onboarding wizard: re-opens after each email verification, until the
  // applicant hits Skip. Always replayable via the link.
  const [wizardOpen, setWizardOpen] = useState(
    store.pendingWelcomeWizard && !store.wizardSkipped
  )

  const closeWizard = (permanent: boolean) => {
    setWizardOpen(false)
    if (permanent) store.skipWelcomeWizard()
    else store.dismissWelcomeWizard()
  }

  // Pre-populate from persisted store
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    // We scroll/focus the first error ourselves (smooth, top-to-bottom), so turn
    // off RHF's default instant focus-jump to avoid a double scroll.
    shouldFocusError: false,
    // Validation only fires on Continue (submit), never live while the user is
    // editing or clearing a field — errors return only on the next Continue.
    // Matches the validation rule used across the other steps.
    reValidateMode: 'onSubmit',
    defaultValues: {
      firstName: (store.formData.firstName as string) ?? '',
      lastName: (store.formData.lastName as string) ?? '',
      jobTitle: (store.formData.jobTitle as string) ?? '',
      companyWebsite:
        (store.formData.companyWebsite as string) ?? store.companyWebsite ?? '',
    } as DefaultValues<FormData>,
  })

  // Persist field to store on change; the auto-save hook picks it up and
  // PATCHes the server (which also drives the SaveIndicator).
  const persistField = (name: keyof FormData, value: string) => {
    store.setField(name, value)
  }

  const onSubmit = (data: FormData) => {
    store.setField('firstName', data.firstName)
    store.setField('lastName', data.lastName)
    store.setField('jobTitle', data.jobTitle)
    store.setField('companyWebsite', data.companyWebsite)
    store.setStep(2)
    navigate('/company-details')
  }

  // On a blocked Continue, smoothly scroll to the topmost field with an error so
  // the user is taken straight to what needs fixing (top-to-bottom priority).
  const onInvalid = () => scrollToFirstError(FIRST_INVALID_FIELD)

  // Auto-prepend https:// on blur if no protocol present
  const handleWebsiteBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const val = e.target.value.trim()
    if (val && !val.startsWith('http://') && !val.startsWith('https://')) {
      const updated = `https://${val}`
      setValue('companyWebsite', updated, { shouldValidate: true })
      store.setField('companyWebsite', updated)
    } else if (val) {
      store.setField('companyWebsite', val)
    }
  }

  // Motion variants
  const containerVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.4,
        ease: [0.0, 0.0, 0.2, 1.0] as [number, number, number, number],
        delay: shouldReduceMotion ? 0 : 0.1,
      },
    },
  }

  // One email can hold several applications, so the banner counts them rather
  // than implying there is only ever one. The session only caches the current
  // application, so the count comes from the same list endpoint My Applications
  // uses. A failed lookup is not worth an error state here — the banner simply
  // falls back to the singular wording it had before.
  const [submittedCount, setSubmittedCount] = useState(0)
  const [latestReference, setLatestReference] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    listApplications()
      .then((res) => {
        if (cancelled) return
        const submitted = res.applications.filter((a) => a.status === 'submitted')
        setSubmittedCount(submitted.length)
        const newest = submitted[0]
        if (newest) setLatestReference(applicationReference(newest.id))
      })
      .catch(() => {
        /* keep the fallback wording */
      })
    return () => {
      cancelled = true
    }
  }, [])

  const hasSubmission = !!store.submittedAt || submittedCount > 0

  return (
    <PageShell showStepper currentStep={0}>
      <WelcomeWizard open={wizardOpen} onClose={closeWizard} />
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* ----------------------------------------------------------------- */}
        {/* Previous submission banner — only shown when history exists         */}
        {/* ----------------------------------------------------------------- */}
        {hasSubmission && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--spacing-4)',
              background: 'var(--color-info-surface)',
              border: '1px solid var(--color-action-primary-focus)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-4) var(--spacing-6)',
              marginBottom: 'var(--spacing-6)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <ClipboardList
                size={20}
                color="var(--color-action-primary)"
                aria-hidden="true"
                style={{ flexShrink: 0 }}
              />
              <div>
                <p style={{
                  margin: 0,
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-body-sm)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--color-info-text)',
                  lineHeight: 'var(--leading-tight)',
                }}>
                  {submittedCount > 1
                    ? `You have ${submittedCount} previous submissions`
                    : 'You have a previous submission'}
                </p>
                <p style={{
                  margin: '2px 0 0',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-label-sm)',
                  color: 'var(--color-text-secondary)',
                }}>
                  {latestReference ? `Reference: ${latestReference}` : 'Reference unavailable'}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/history')}
              style={{ flexShrink: 0 }}
            >
              View submission
            </Button>
          </div>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* Block 1 — Section Header                                           */}
        {/* ----------------------------------------------------------------- */}
        <div style={{ marginBottom: 'var(--spacing-8)' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--spacing-6)' }}>
            <Button variant="tertiary" onClick={() => setWizardOpen(true)}>
              Replay walkthrough
            </Button>
          </div>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 'var(--weight-semibold)',
              fontSize: 'var(--text-display-sm)',
              color: 'var(--color-text-primary)',
              lineHeight: 'var(--leading-tight)',
              margin: '0 0 var(--spacing-3) 0',
            }}
          >
            Welcome to your Zota application
          </h1>
          <p
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body-md)',
              color: 'var(--color-text-secondary)',
              lineHeight: 'var(--leading-normal)',
              margin: 0,
            }}
          >
            This application takes{' '}
            <strong style={{ fontWeight: 'var(--weight-semibold)' }}>less than 5 minutes</strong>{' '}
            if you have your documents ready. We will save your progress
            automatically — you can close the browser and pick up where you left
            off at any time.
          </p>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Block 2 — "What you'll need" Card                                  */}
        {/* ----------------------------------------------------------------- */}
        <Card
          style={{ marginBottom: 'var(--spacing-8)', padding: 'var(--spacing-8)' }}
        >
          {/* Card header — clickable toggle */}
          <button
            type="button"
            onClick={() => setChecklistOpen((prev) => !prev)}
            aria-expanded={checklistOpen}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              marginBottom: checklistOpen ? 'var(--spacing-8)' : 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <Info
                size={20}
                aria-hidden="true"
                style={{ color: 'var(--color-action-primary)', flexShrink: 0 }}
              />
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 'var(--weight-semibold)',
                  fontSize: 'var(--text-title-md)',
                  color: 'var(--color-text-primary)',
                  lineHeight: 'var(--leading-tight)',
                  margin: 0,
                }}
              >
                {checklistOpen ? 'Documents to prepare' : 'Check which documents to prepare'}
              </h2>
            </div>
            <ChevronDown
              size={18}
              aria-hidden="true"
              style={{
                color: 'var(--color-text-secondary)',
                flexShrink: 0,
                transform: checklistOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 250ms ease',
              }}
            />
          </button>

          {/* Collapsible content */}
          {checklistOpen && (
            <>
          {/* Document list — no dividers, spacing only */}
          <ul
            role="list"
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-4)',
            }}
          >
            {DOCS.map((doc) => (
              <li
                key={doc.title}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--spacing-4)',
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--color-surface-container-low)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  <FileText
                    size={20}
                    aria-hidden="true"
                    style={{ color: 'var(--color-action-primary)' }}
                  />
                </div>

                {/* Text column */}
                <div>
                  <div style={{ marginBottom: 'var(--spacing-2)' }}>
                    <p
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontWeight: 'var(--weight-semibold)',
                        fontSize: 'var(--text-body-md)',
                        color: 'var(--color-text-primary)',
                        lineHeight: 'var(--leading-normal)',
                        margin: 0,
                      }}
                    >
                      {doc.title.split(' ').slice(0, -1).join(' ')}{' '}
                      {/* Keep the last word and the badge together so the badge
                          never sits alone on its own line. */}
                      <span style={{ whiteSpace: 'nowrap' }}>
                        {doc.title.split(' ').slice(-1)[0]}
                        <Badge
                          variant={doc.required ? 'required' : 'optional'}
                          size="sm"
                          style={{ verticalAlign: 'middle', marginLeft: 'var(--spacing-1)' }}
                        >
                          {doc.required ? 'Required' : 'Optional'}
                        </Badge>
                      </span>
                    </p>
                  </div>
                  <p
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-label-sm)',
                      color: 'var(--color-text-secondary)',
                      lineHeight: 'var(--leading-normal)',
                      margin: 0,
                    }}
                  >
                    {doc.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          {/* Checklist footer note */}
          <p
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-label-sm)',
              color: 'var(--color-text-tertiary)',
              lineHeight: 'var(--leading-normal)',
              marginTop: 'var(--spacing-6)',
              marginBottom: 0,
            }}
          >
            Not sure where to find a document? Your company secretary or legal
            counsel can usually help.
          </p>
            </>
          )}
        </Card>

        {/* ----------------------------------------------------------------- */}
        {/* Block 3 — Contact Information Form                                 */}
        {/* ----------------------------------------------------------------- */}
        <form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
          {/* Contact details — wrapped in the same surface container used by the
              following steps (see DocSection in CompanyDetailsScreen) so the
              Welcome form reads as a consistent card. */}
          <section
            style={{
              background: 'var(--color-surface-container-lowest)',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--spacing-8)',
              marginBottom: 'var(--spacing-8)',
            }}
          >
            {/* Section heading — matches the section titles used on the
                following step cards (see DocSection in CompanyDetailsScreen). */}
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-title-md)',
                fontWeight: 'var(--weight-semibold)',
                color: 'var(--color-text-primary)',
                lineHeight: 'var(--leading-tight)',
                margin: '0 0 var(--spacing-8) 0',
              }}
            >
              Your contact details
            </h2>

            {/* Fields */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-6)',
              }}
            >
            {/* First name */}
            <FormField
              label="First name"
              name="firstName"
              required
              error={errors.firstName?.message}
            >
              <input
                id="firstName"
                type="text"
                autoComplete="given-name"
                placeholder="e.g. Maria"
                aria-invalid={errors.firstName ? 'true' : 'false'}
                className={inputClassName}
                {...register('firstName', {
                  onChange: (e) => persistField('firstName', e.target.value),
                })}
              />
            </FormField>

            {/* Last name */}
            <FormField
              label="Last name"
              name="lastName"
              required
              error={errors.lastName?.message}
            >
              <input
                id="lastName"
                type="text"
                autoComplete="family-name"
                placeholder="e.g. Andersen"
                aria-invalid={errors.lastName ? 'true' : 'false'}
                className={inputClassName}
                {...register('lastName', {
                  onChange: (e) => persistField('lastName', e.target.value),
                })}
              />
            </FormField>

            {/* Job title */}
            <FormField
              label="Job title"
              name="jobTitle"
              required
              error={errors.jobTitle?.message}
              helperText="Your current role at the company."
            >
              <input
                id="jobTitle"
                type="text"
                autoComplete="organization-title"
                placeholder="e.g. Head of Compliance"
                aria-invalid={errors.jobTitle ? 'true' : 'false'}
                className={inputClassName}
                {...register('jobTitle', {
                  onChange: (e) => persistField('jobTitle', e.target.value),
                })}
              />
            </FormField>

            {/* Company Email — read-only, locked */}
            <FormField
              label="Company email address"
              name="authEmail"
              helperText="This is the email you used to log in. It cannot be changed here."
            >
              <div style={{ position: 'relative' }}>
                <input
                  id="authEmail"
                  type="email"
                  readOnly
                  value={(store.formData.authEmail as string) ?? store.authEmail ?? ''}
                  tabIndex={-1}
                  className={inputClassName}
                  // `user-select: none` is ignored on <input> in Chrome/Safari, so the
                  // value can still be drag- or double-click-selected. Blocking the
                  // mousedown stops the browser from focusing the field and starting a
                  // selection, which is what actually keeps the locked value unselectable.
                  onMouseDown={(e) => e.preventDefault()}
                  style={{
                    background: 'var(--color-field-background-disabled)',
                    color: 'var(--color-text-secondary)',
                    cursor: 'not-allowed',
                    paddingRight: 'var(--spacing-10)',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                  }}
                />
                {/* Lock icon */}
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--color-text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="7" width="10" height="8" rx="1.5" fill="currentColor" opacity="0.5"/>
                    <path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.5"/>
                  </svg>
                </span>
              </div>
            </FormField>

            {/* Company Website */}
            <FormField
              label="Company website"
              name="companyWebsite"
              required
              error={errors.companyWebsite?.message}
              helperText="Enter your company's main website URL."
            >
              <input
                id="companyWebsite"
                type="url"
                autoComplete="url"
                placeholder="https://example.com"
                aria-invalid={errors.companyWebsite ? 'true' : 'false'}
                className={inputClassName}
                {...register('companyWebsite', {
                  onChange: (e) => {
                    store.setField('companyWebsite', e.target.value)
                  },
                  onBlur: handleWebsiteBlur,
                })}
              />
            </FormField>
            </div>
          </section>

          {/* ---------------------------------------------------------------- */}
          {/* Block 5 — SaveIndicator + Block 4 — CTA                          */}
          {/* ---------------------------------------------------------------- */}
          {/* Pulled up so the required-fields note hugs the card above, with
              clear separation before the button. */}
          <div style={{ position: 'relative', marginTop: 'calc(-1 * var(--spacing-4))' }}>
            {/* Required-fields note (left) + SaveIndicator (right), bottom-aligned on one row */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: 'var(--spacing-4)',
                marginBottom: 'var(--spacing-10)',
                minHeight: '20px',
              }}
            >
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-label-sm)',
                color: 'var(--color-text-tertiary)',
                margin: 0,
              }}>
                Fields marked with <span style={{ color: 'var(--color-action-primary)' }}>*</span> are required.
              </p>
              <SaveIndicator state={saveState} />
            </div>
            {/* CTA button — full width */}
            <Button
              variant="primary"
              type="submit"
              style={{ width: '100%' }}
            >
              Continue to Company Details
            </Button>
          </div>
        </form>
      </motion.div>
    </PageShell>
  )
}
